import { Component, forwardRef, OnChanges, KeyValueDiffers, OnInit, OnDestroy, ChangeDetectorRef, Inject, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { APP_BASE_HREF } from '@angular/common';

import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { MonitorConfigService } from '../../../MonitorShared/services/monitor-config.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';

import { ReportsSeriesPreviewService } from '../../../Reports/services/report-series-preview.service';
import { ReportBlockTypeEnum } from '../../../Reports/enums/report-block-type.enum';
import { ChartDisplayElementModel } from '../../../Reports/models/chart-display-element.model';
import { AbstractPageBuilderService } from '../../../PageBuilder/services/abstract-page-builder.service';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { ConfirmDialogModel } from '../../../Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ReportsChartOptionsService } from '../../../Reports/services/reports-chart-options.service';
import { MultiSeriesDateAndValueXYChart } from '../../../Charts/models/multi-series-date-and-value-x-y.chart';
import { ChartBlockComponent } from '../../../Reports/components/ChartBlock/chart-block.component';
import { ProminenceLockerService } from '../../../Misc/services/prominence-locker.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { ClickOutsideService } from '../../../../Common/PageBuilder/services/click-outside.service';
import { ReportsChartDetailService } from '../../../Reports/services/reports-chart-detail.service';
import { SocialChartDetailsDialogService } from '../../../../Social/Intelligence/services/social-chart-details-dialog.service';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { DataConnectionService } from '../../../../Core/Users/services/data-connection.service';
import { ReportsChartSeriesConverterService } from '../../../Reports/services/reports-chart-series-converter.service';
import { ReportsSeriesPreviewNewService } from '../../../../Common/Reports/services/reports-series-preview-new.service';
import { DashboardPageBuilderService } from '@Common/Dashboard/services/dashboard-page-builder.service';


@Component({
    selector: 'agpr-dashboard-chart-block',
    templateUrl: './dashboard-chart-block.component.html',
    styleUrls: ['./dashboard-chart-block.component.scss'],
    providers: [
        {
            provide: ChartBlockComponent,
            useExisting: forwardRef(() => DashboardChartBlockComponent)
        }
    ],
    standalone: false
})
export class DashboardChartBlockComponent extends ChartBlockComponent implements OnInit, OnChanges, OnDestroy {

    readonly buttonTypes = ButtonTypesEnum;
    small: boolean = false;

    chartResizeObserver = new ResizeObserver(entries => {
        const width = entries[0].contentRect.width;
        const height = entries[0].contentRect.height;
        if (width <= 300 || height <= 205) {
            if (!entries[0].target.classList.contains('agpr-chartBlock-small')) {
                entries[0].target.classList.add('agpr-chartBlock-small');
                this.small = true;
            }
        } else {
            entries[0].target.classList.remove('agpr-chartBlock-small');
            this.small = false;
        }
        this.changeDetectorRef.detectChanges();
    });

    constructor(
        public differs: KeyValueDiffers,
        public pageBuilderService: AbstractPageBuilderService,
        public reportSeriesPreviewService: ReportsSeriesPreviewService,
        public reportsChartOptionsService: ReportsChartOptionsService,
        public localizationService: LocalizationService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public changeDetectorRef: ChangeDetectorRef,
        public userStateService: CurrentUserStateService,
        public prominenceLockerService: ProminenceLockerService,
        public monitorConfigService: MonitorConfigService,
        public coreDialogService: CoreDialogService,
        protected userPreferencesService: UserPreferencesService,
        public router: Router,
        public clickOutsideService: ClickOutsideService,
        public reportsChartDetailService: ReportsChartDetailService,
        public socialChartDetailsDialogService: SocialChartDetailsDialogService,
        public configService: ConfigService,
        public elementRef: ElementRef,
        public dataConnectionService: DataConnectionService,
        public reportsChartSeriesConverterService: ReportsChartSeriesConverterService,
        public reportsSeriesPreviewNewService: ReportsSeriesPreviewNewService
    ) {
        super(
            differs,
            pageBuilderService,
            reportSeriesPreviewService,
            reportsChartOptionsService,
            localizationService,
            appBaseHref,
            changeDetectorRef,
            userStateService,
            prominenceLockerService,
            monitorConfigService,
            userPreferencesService,
            clickOutsideService,
            reportsChartDetailService,
            socialChartDetailsDialogService,
            configService,
            elementRef,
            dataConnectionService,
            reportsChartSeriesConverterService,
            reportsSeriesPreviewNewService
        );

        this.subscriptions.push(
            this.pageBuilderService.getSelectedBlockUpdated().subscribe(() => {
                if (
                    this.pageBuilderService.selectedBlock === this.block &&
                    this.block?.type?.key === ReportBlockTypeEnum.RECENT_TOPICS
                ) {
                    this.updateChart();
                }
            }),
            this.pageBuilderService.getBlocks().subscribe((blocks) => {
                if (this.block && this.block?.type?.key === ReportBlockTypeEnum.RECENT_TOPICS) {
                    this.updateChart();
                }
            })
        );
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.chartResizeObserver.observe(this.elementRef.nativeElement?.children?.[0]);
    }

    ngOnChanges(): void {
        super.ngOnChanges();
        this.updateChart();
    }

    updateChart() {
        if (this.block.type.key === ReportBlockTypeEnum.RECENT_TOPICS) {
            this.renderable = true;
            this.displayElement = this.block?.displayElement as ChartDisplayElementModel;

            if (
                this.lastDisplayElement?.isEqual(this.displayElement.clone()) ||
                this.displayElement?.series?.findIndex(s => s.label === null || s.label?.length === 0) !== -1
            ) {
                // chart hasn't changed or one or more series don't have labels, don't make a new one
                return;
            }

            if (this.displayElement?.series?.length < 1) {
                this.chart = null;
                this.lastDisplayElement = null;
                return;
            }
            let series = this.displayElement.series;

            // cache the display element so we can verify if we actually need to refresh the chart
            this.lastDisplayElement = this.displayElement?.clone();
            this.setLabels();

            if (this.preview) {
                series = this.reportSeriesPreviewService.getPreviewSeries(this.block.type.key as ReportBlockTypeEnum, series);
            }

            this.chart = new MultiSeriesDateAndValueXYChart(
                series,
                this.reportsChartOptionsService.getRecentTopicChartOptions(
                    this.displayElement
                ),
                this.getChartMetadata()
            );
            if ((this.pageBuilderService as DashboardPageBuilderService).mapBlockToChartComponent) {
                // If it's injected as DashboardPageBuilderService it causes a circular dependency, casting works though
                (this.pageBuilderService as DashboardPageBuilderService).mapBlockToChartComponent(this.block, this);
            }
        } else {
            super.updateChart();
        }
    }

    doAction($event: MouseEvent | TouchEvent, action: string): void {
        // we only want the left mouse click
        if ($event.which === 1) {
            if (action === 'delete') {
                this.deleteChart(action);
            } else {
                this.onAction(action);
            }
        }
    }

    deleteChart(action: string) {
        const confirmConfig: ConfirmDialogModel = {
            inputs: {
                id: 'dashboard.deleteChartConfirmDialog',
                heading: this.localizationService.transform('Dashboard.confirmDialog.deleteChart.heading'),
                message: this.localizationService.transform('Dashboard.confirmDialog.deleteChart.message'),
                alertType: messageSeverityEnum.warning,
                okLabel: this.localizationService.transform('Dashboard.confirmDialog.deleteChart.button')
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.onAction(action);
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
    }

    navigateTo() {
        this.router.navigate(['Monitoring/search']);
    }

    dragKeydown(event: KeyboardEvent): void {
        super.dragKeydown(event);
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.chartResizeObserver.unobserve(this.elementRef.nativeElement?.children?.[0]);
        this.chartResizeObserver.disconnect();
    }
}
